import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { useState } from 'react';
import { message } from 'antd';
import { ATTENDANCE_MODES, CHECK_IN_METHODS } from 'shared/constants/schedule.constants';
import { EVENTS, SCHEDULE_EVENT_TYPES } from 'shared/constants';
import {
  useMyCheckIn,
  useSelfAttestation,
  CHECK_IN_RESULT,
  CHECK_INS_POLL_INTERVAL_MS,
  checkedInCountInterval,
} from 'shared/hooks/attendance.hooks';
import { useScheduleEventDetail } from 'shared/hooks/schedule-events.hooks';
import { Button } from 'src/components/design-system';
import analyticsService from 'src/helpers/analytics.service';
import monitorService from 'src/helpers/monitor.service';
import { useEducationalEventsContext } from '../../educational-events-context';
import { useEducationalEventFormat } from '../../helpers/use-educational-event-format';
import {
  canSelfAttest,
  getCheckInSchedule,
  isCheckInEligibleToday,
  getCheckInModeFromEvent,
} from '../../helpers/event-view.helpers';
import { AttendanceModePromptModal } from './attendance-mode-prompt-modal';
import panelStyles from '../event-detail-panel/event-detail-panel.module.scss';
import styles from './check-in.module.scss';

export function SelfAttestButton({ event }) {
  const { lang, refreshSelectedEvent } = useEducationalEventsContext();
  const { formatTimeOrDateTime } = useEducationalEventFormat();
  const [modePromptOpen, setModePromptOpen] = useState(false);

  const selfAttestationAllowed = event?.attributes?.allowSelfAttestation === true;
  const canAttest = canSelfAttest(event);
  const { data: myCheckIn, isFetched } = useMyCheckIn(event?.id, {
    enabled: selfAttestationAllowed,
  });

  const checkedIn = Boolean(myCheckIn?.checkedIn);

  /**
   * A second observer on the event detail query the page already holds
   * (`educational-events-context.js`) — react-query applies the smallest
   * `refetchInterval` across a key's observers, so this raises the refresh
   * rate only while the button is mounted and could still become actionable,
   * without touching the shared 5-minute staleTime default.
   */
  useScheduleEventDetail(event?.id, {
    enabled: selfAttestationAllowed && !checkedIn && isCheckInEligibleToday(event),
    refetchInterval: query =>
      checkedInCountInterval(CHECK_INS_POLL_INTERVAL_MS, query.state.fetchFailureCount),
    refetchOnWindowFocus: true,
  });

  const { submit, submitting } = useSelfAttestation(event?.id, {
    onError: (error, context) =>
      monitorService.error('Self attestation failed', { error, ...context }),
  });

  const { opensAt } = getCheckInSchedule(event);
  const awaitingScheduledOpen = selfAttestationAllowed && !checkedIn && !canAttest && !!opensAt;

  if (awaitingScheduledOpen) {
    return (
      <div className={panelStyles.checkInRow}>
        <div className={panelStyles.checkInText}>
          <span className={panelStyles.checkInHint}>
            {lang.CHECK_IN_OPENS_AT.replace('{time}', formatTimeOrDateTime(opensAt))}
          </span>
        </div>
      </div>
    );
  }

  if (!checkedIn && !canAttest) return null;

  const submitAttendanceMode = async attendanceMode => {
    const { result } = await submit({ attendanceMode });
    analyticsService.track(EVENTS.SCHEDULE.CHECK_IN_SUBMIT, {
      type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
      eventId: event.id,
      method: CHECK_IN_METHODS.SELF_ATTESTATION,
      result,
      checkInType: attendanceMode === ATTENDANCE_MODES.VIRTUAL ? 'virtual' : 'on-site',
    });
    if (result === CHECK_IN_RESULT.SESSION_CLOSED) {
      message.error(lang.CHECK_IN_NOT_OPEN);
      refreshSelectedEvent();
    } else if (result === CHECK_IN_RESULT.DISABLED) {
      message.error(lang.SELF_ATTESTATION_DISABLED);
      refreshSelectedEvent();
    } else if (result === CHECK_IN_RESULT.FAILED) {
      message.error(lang.SELF_CHECK_IN_FAILED);
    }
  };

  const handleClick = () => {
    const mode = getCheckInModeFromEvent({ event });
    if (mode) {
      submitAttendanceMode(mode);
    } else {
      setModePromptOpen(true);
    }
  };

  const handlePickMode = attendanceMode => {
    setModePromptOpen(false);
    submitAttendanceMode(attendanceMode);
  };

  return (
    <div className={panelStyles.checkInRow}>
      {checkedIn ? (
        <span className={styles.selfAttestDone} role="status">
          <FontAwesomeIcon icon={faCircleCheck} className={styles.selfAttestDoneIcon} />
          {lang.CHECK_IN_SELF_DONE}
        </span>
      ) : (
        <>
          <div className={panelStyles.checkInText}>
            <span className={panelStyles.checkInTitle}>{lang.SESSION_OPEN}</span>
            <span className={panelStyles.checkInHint}>{lang.SELF_ATTEST_PROMPT}</span>
          </div>
          <div className={panelStyles.checkInActions}>
            <Button
              size="small"
              type="primary"
              disabled={!isFetched || submitting}
              loading={submitting}
              onClick={handleClick}>
              {lang.CHECK_IN}
            </Button>
          </div>
        </>
      )}
      <AttendanceModePromptModal
        open={modePromptOpen}
        onPick={handlePickMode}
        onCancel={() => setModePromptOpen(false)}
      />
    </div>
  );
}
